import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Add, Edit, Assignment, Delete, Download } from "@mui/icons-material";
import { collection, onSnapshot, deleteDoc, doc, addDoc } from "firebase/firestore";
import { firestore as db } from "../components/firebase.config"
import ChecklistForm from "./ChecklistForm"; // Import the ChecklistForm component
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const AdminChecklistView = () => {
  const [checklists, setChecklists] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false); // State to manage form visibility
  const [selectedChecklist, setSelectedChecklist] = useState(null); // State for editing checklist
  const [assigningChecklist, setAssigningChecklist] = useState(null); // State for assigning checklist
  const [assignEmail, setAssignEmail] = useState(""); // Email for assignment
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false); // State for assignment dialog

  useEffect(() => {
    // Fetching checklists created by the admin
    const unsubscribe = onSnapshot(collection(db, "checklists"), (snapshot) => {
      const fetchedChecklists = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setChecklists(fetchedChecklists);
    });

    return () => unsubscribe();
  }, []);

  const handleDeleteChecklist = async (id) => {
    // Deleting checklist from Firestore
    await deleteDoc(doc(db, "checklists", id));
  };

  const generatePDF = (checklist) => {
    const doc = new jsPDF();

    // Set headers based on the columns
    const headers = checklist.columns.map((col) => col.name);

    // Generate rows based on the data in each column for each row
    const rows = checklist.rows.map((row) =>
      checklist.columns.map((col) => row.data[col.id] || "")
    );

    // Add the checklist name as the title
    doc.text(checklist.name, 10, 10);

    // Create the table with the headers and rows
    autoTable(doc,{
      head: [headers],
      body: rows,
      startY: 20, // Start the table after the title
      theme: "grid", // Apply grid theme to make the table look better
    });

    // Add remarks section at the bottom of the table
    let yOffset = doc.lastAutoTable.finalY + 10; // Position remarks after the table

    if (checklist.remarks) {
      doc.text(`Remarks: ${checklist.remarks}`, 10, yOffset);
      yOffset += 10; // Increase Y offset to add space between remarks and images
    }

    // Add images if present
    if (checklist.images && checklist.images.length > 0) {
      checklist.images.forEach((image, index) => {
        // Ensure the image is base64 encoded or URL
        doc.addImage(image, "JPEG", 10, yOffset, 50, 50); // Adjust size as needed
        yOffset += 55; // Adjust the Y position for the next image
      });
    }

    // Save the PDF with the checklist name
    const fileName = checklist.name ? `${checklist.name}.pdf` : "Checklist.pdf";
    doc.save(fileName);
  };

  const handleAssign = async () => {
    if (!assignEmail || !assigningChecklist) return;

    const newChecklist = {
      ...assigningChecklist,
      originalChecklistId: assigningChecklist.id,
      assignedTo: assignEmail,
    };

    delete newChecklist.id; // Remove original ID to create a new document
    await addDoc(collection(db, "assignedChecklists"), newChecklist);

    setAssigningChecklist(null);
    setAssignEmail("");
    setIsAssignDialogOpen(false);
  };

  const openForm = () => {
    setSelectedChecklist(null); // Clear selected checklist for a new form
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  return (
    <div>
      {/* Add Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <Button
          onClick={openForm} // Trigger form opening
          sx={{
            backgroundColor: "blue",
            color: "white",
            borderRadius: "50%",
            minWidth: "60px",
            minHeight: "60px",
            fontSize: "1.5rem",
            "&:hover": { backgroundColor: "darkblue" },
          }}
        >
          <Add />
        </Button>
      </Box>

      {/* Checklist Form */}
      {isFormOpen && (
        <ChecklistForm
          email="admin@example.com" // Example email, replace with dynamic user email
          onClose={closeForm}
          selectedChecklist={selectedChecklist}
        />
      )}

      {/* Assignment Dialog */}
      <Dialog open={isAssignDialogOpen} onClose={() => setIsAssignDialogOpen(false)}>
        <DialogTitle>Assign Checklist</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            value={assignEmail}
            onChange={(e) => setAssignEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAssign} color="primary">
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Created Checklists Section */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Created Checklists
        </Typography>
        <Paper sx={{ padding: "1rem", backgroundColor: "#f5f5f5" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checklists.map((checklist) => (
                  <TableRow key={checklist.id}>
                    <TableCell>{checklist.name}</TableCell>
                    <TableCell>{checklist.description}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          onClick={() => {
                            setSelectedChecklist(checklist);
                            setIsFormOpen(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Assign">
                        <IconButton
                          color="info"
                          onClick={() => {
                            setAssigningChecklist(checklist);
                            setIsAssignDialogOpen(true);
                          }}
                        >
                          <Assignment />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteChecklist(checklist.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          color="secondary"
                          onClick={() => generatePDF(checklist)}
                        >
                          <Download />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </div>
  );
};

export default AdminChecklistView;
